import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Skeleton from '@mui/material/Skeleton';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from '../../contexts/SnackbarContext';


import V3ClipboardCell from "./V3ClipboardCell";
import { fetchGoto, fetchGotoCaravan, withAuthCheck, AuthError } from '../../services/api';
import RichText from "../../components/RichText";
import ItemIconV3 from './ItemIconV3';
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { v3Theme } from '../theme';

// CSS for loading button animations
const travelingButtonStyles = `
    @keyframes v3-walking {
        0%, 100% { transform: rotate(-8deg); }
        50% { transform: rotate(8deg); }
    }

    @keyframes v3-shimmer {
        0% { left: -100%; }
        100% { left: 100%; }
    }

    .loading-button-fake {
        position: relative;
        overflow: hidden;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 6px 16px;
        border-radius: 4px;
        font-size: 0.875rem;
        font-weight: 500;
        cursor: default;
        min-width: 64px;
        box-sizing: border-box;
        line-height: 1.75;
        letter-spacing: 0.02857em;
        box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12);
    }

    .loading-button-fake::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%);
        animation: v3-shimmer 1.5s ease-in-out infinite;
    }

    .walking-icon {
        animation: v3-walking 0.4s ease-in-out infinite !important;
        transform-origin: 50% 100% !important;
        display: inline-block !important;
    }
`;

const V3RegionDetails = ({
                           open,
                           onClose,
                           title,
                           subtitle,
                           icon,
                           extraInfo,
                           routeInfo,
                           steps,
                           commandValue,
                           canTravel,
                           isLoading,
                           token,
                           pointId,
                           lootstat,
                            canCaravan,
                            caravanCost,
                            caravanDuration,
                       }) => {
    const showSkeleton = isLoading || !title || !subtitle;
    const [isTraveling, setIsTraveling] = useState(false);
    const { showSnackbar } = useSnackbar();

    const handleGotoClick = () => {
        setIsTraveling(true);
        withAuthCheck(fetchGoto, token, pointId)
            .then(data => {
                showSnackbar(data.result, "success");
                onClose();
                setIsTraveling(false);
            })
            .catch(err => {
                if (err instanceof AuthError) {
                    setIsTraveling(false);
                    return;
                }
                //console.error("Error on goto:", err);
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
                onClose();
                setIsTraveling(false);
            });
    };

    const handleCaravanClick = () => {
        setIsTraveling(true);
        withAuthCheck(fetchGotoCaravan, token, pointId)
            .then(data => {
                showSnackbar(data.result, "success");
                onClose();
                setIsTraveling(false);
            })
            .catch(err => {
                if (err instanceof AuthError) {
                    setIsTraveling(false);
                    return;
                }
                //console.error("Error on goto:", err);
                const errorMessage = err.result || err.message || "error";
                showSnackbar(errorMessage, "error");
                onClose();
                setIsTraveling(false);
            });
    };


    return (
        <>
            <style>{travelingButtonStyles}</style>
            <Dialog
                open={open}
                onClose={onClose} 
                maxWidth={false}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        left: 8,
                        right: 8,
                        top: { xs: 80, sm: 100 },
                        bottom: { xs: 80, sm: 100 },
                        margin: 0,
                        maxWidth: 'none',
                        maxHeight: { xs: 'calc(100vh - 160px)', sm: 'calc(100vh - 200px)' },
                        width: 'auto',
                    }
                }}
            >
                {showSkeleton ? (
                    // Показываем скелетон на весь экран
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2
                    }}>
                        <Skeleton variant="rectangular" height={50} />
                        <Skeleton variant="rectangular" height={100} />
                        <Skeleton variant="rectangular" height={100} />
                        <Skeleton variant="rectangular" height={50} />
                    </Box>
                ) : (
                    // Когда данные загружены, показываем контент
                    <Box
                        sx={{
                            bgcolor: '#fff',
                            overflow: 'visible',
                            py: { xs: 2, sm: 3, md: 4 },
                            px: { xs: 0.5, sm: 1, md: 2 },
                            boxSizing: 'border-box',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: 2, sm: 3, md: 4 },
                            fontFamily: v3Theme.typography.fontFamily,
                        }}
                    >
                        {/* Region Card Header - embedded from V2 RegionCard with V3 theme */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                width: '100%',
                                height: 'auto',
                                position: 'relative',
                                margin: '0 auto',
                                marginTop: 3, // Add space for icon overflow
                                backgroundColor: '#fff',
                                border: `1px solid ${v3Theme.border.light}`,
                                borderRadius: 1,
                                overflow: 'visible',
                                zIndex: 1,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)', // More visible shadow
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: `75%`, sm: `85%`, md: `90%` },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    padding: 2,
                                }}
                            >
                                <Typography variant="subtitle2" sx={{
                                    fontFamily: v3Theme.typography.fontFamily,
                                    color: v3Theme.text.primary,
                                    fontWeight: 600
                                }}>
                                    <RichText content={title || '-'} />
                                </Typography>
                                <Typography variant="subtitle2" sx={{
                                    fontFamily: v3Theme.typography.fontFamily,
                                    color: v3Theme.text.primary
                                }}>
                                    <RichText content={subtitle || '-'} />
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    width: { xs: '25%', sm: '15%', md: 80 },
                                    minWidth: 64,
                                    maxWidth: 120,
                                    position: 'relative',
                                    height: 'auto',
                                    aspectRatio: '1',
                                    overflow: 'visible',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                }}
                            >
                                {icon && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            position: 'relative',
                                            overflow: 'visible',
                                        }}
                                    >
                                        <img
                                            src={`/img/map/${icon}${icon.includes('.') ? '' : '.png'}`}
                                            alt="Region"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                borderRadius: '4px',
                                                maxHeight: '150%', // Allow image to be taller than container
                                                zIndex: 10,
                                            }}
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </Box>
                                )}

                                {/* Skeleton while loading */}
                                {!icon && (
                                    <Skeleton
                                        variant="rectangular"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            top: 0,
                                            borderRadius: 1,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Card sx={{ 
                            p: { xs: 0.5, sm: 1, md: 2 }, 
                            borderRadius: 2, 
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                            backgroundColor: '#fff',
                            border: `1px solid ${v3Theme.border.light}`
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{ 
                                        color: v3Theme.text.primary,
                                        fontFamily: v3Theme.typography.fontFamily 
                                    }}
                                    dangerouslySetInnerHTML={{ __html: routeInfo }}
                                />

                                {isTraveling ? (
                                    <div
                                        className="loading-button-fake"
                                        style={{
                                            backgroundColor: '#bdc3c7',
                                            color: '#2c3e50',
                                            fontFamily: v3Theme.typography.fontFamily,
                                            width: 'fit-content',
                                            alignSelf: 'flex-start',
                                        }}
                                    >
                                        <b style={{ position: 'relative', zIndex: 1 }}>{steps}</b>
                                        <img
                                            src="/img/steps.webp"
                                            alt="Steps"
                                            className="walking-icon"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                marginLeft: '8px',
                                                position: 'relative',
                                                zIndex: 1
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        onClick={handleGotoClick}
                                        sx={{
                                            alignSelf: 'flex-start',
                                            fontFamily: v3Theme.typography.fontFamily,
                                            color: '#fff',
                                            backgroundColor: v3Theme.main,
                                            '&:hover': {
                                                backgroundColor: v3Theme.main
                                            },
                                            '&:disabled': {
                                                backgroundColor: v3Theme.text.disabled,
                                                color: '#fff'
                                            }
                                        }}
                                        disabled={canTravel === "false"}
                                    >
                                        <b>{steps}</b>
                                        <Box
                                            component="img"
                                            src="/img/steps.webp"
                                            alt="Steps"
                                            sx={{
                                                width: '20px',
                                                height: '20px',
                                                marginLeft: '8px',
                                                filter: 'brightness(0) invert(1)'
                                            }}
                                        />
                                    </Button>
                                )}
                                {canCaravan === "true" && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2" sx={{ 
                                            color: v3Theme.text.primary,
                                            fontFamily: v3Theme.typography.fontFamily 
                                        }}>
                                            Caravan services are available in this region.<br/>{caravanDuration} min
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            onClick={handleCaravanClick}
                                            disabled={isTraveling}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                mt: 1,
                                                backgroundColor: v3Theme.main,
                                                color: '#fff',
                                                fontFamily: v3Theme.typography.fontFamily,
                                                '&:hover': {
                                                    backgroundColor: v3Theme.main
                                                },
                                                '&:disabled': {
                                                    backgroundColor: v3Theme.text.disabled
                                                }
                                            }}
                                        >
                                            <b>{caravanCost}</b>
                                                <Box component="img" src="/img/gold.png" alt="Money Icon" sx={{ width: '20px', height: '20px', marginLeft: '8px' }} />
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {extraInfo !== "-" && (
                            <Card sx={{ 
                                p: { xs: 0.5, sm: 1, md: 2 }, 
                                borderRadius: 2, 
                                boxShadow: 'none',
                                backgroundColor: '#fff',
                                border: `1px solid ${v3Theme.border.light}`
                            }}>
                                <CardContent sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0,
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ 
                                            color: v3Theme.text.primary,
                                            fontFamily: v3Theme.typography.fontFamily 
                                        }}
                                    >
                                        <RichText content={extraInfo} />
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}

                        <Card sx={{ 
                            p: { xs: 0.5, sm: 1, md: 2 }, 
                            borderRadius: 2, 
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                            backgroundColor: '#fff',
                            border: `1px solid ${v3Theme.border.light}`
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1
                            }}>
                                <Typography variant="body2" sx={{ 
                                    color: v3Theme.text.primary,
                                    fontFamily: v3Theme.typography.fontFamily 
                                }}>
                                    Bot Command
                                </Typography>
                                <V3ClipboardCell text={commandValue}></V3ClipboardCell>
                            </CardContent>
                        </Card>


                        {lootstat && Array.isArray(lootstat) && lootstat.length > 0 && (
                            <Card sx={{ 
                                p: { xs: 0, sm: 0.5, md: 2 }, 
                                borderRadius: 2, 
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)', 
                                mb: 2,
                                backgroundColor: '#fff',
                                border: `1px solid ${v3Theme.border.light}`
                            }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {lootstat.map((table, tableIdx) => (
                                        <React.Fragment key={tableIdx}>
                                            <Typography variant="body2" sx={{ 
                                                mb: 1,
                                                color: v3Theme.text.primary,
                                                fontFamily: v3Theme.typography.fontFamily 
                                            }}>
                                                {table.title}
                                            </Typography>
                                            <Table size="small" sx={{ mb: tableIdx !== lootstat.length - 1 ? 2 : 0 }}>
                                                <TableBody>
                                                    {table.elems && table.elems.map((elem, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell sx={{ width: '80%', verticalAlign: 'middle' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box sx={{ mr: 1 }}>
                                                                        <ItemIconV3
                                                                            icon={elem.icon}
                                                                            size={24}
                                                                        />
                                                                    </Box>
                                                                    <Typography variant="caption" sx={{ 
                                                                        color: v3Theme.text.primary,
                                                                        fontFamily: v3Theme.typography.fontFamily 
                                                                    }}>
                                                                        <RichText content={elem.name} />
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="caption" sx={{ 
                                                                    color: v3Theme.text.primary,
                                                                    fontFamily: v3Theme.typography.fontFamily 
                                                                }}>
                                                                    <RichText content={elem.value} />
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </React.Fragment>
                                    ))}
                                </CardContent>
                            </Card>
                        )}

                    </Box>
                )}
            </Dialog>

            {/* Бэкдроп с лоадером, показывается пока идет запрос */}
            <Backdrop open={isTraveling} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
};

export default V3RegionDetails;